import {
  ActionIcon,
  Alert,
  Anchor,
  AppShell,
  Button,
  Container,
  Group,
  Image,
  Menu,
  SegmentedControl,
  Text,
  useMantineColorScheme,
} from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { localised, useBrand } from "../brand/brand";
import { rememberLanguage } from "../i18n/i18n";
import { useMe } from "../session/me";
import { useSession } from "../session/session";
import { SETUP_AUTHENTICATOR_PATH } from "../session/totp";
import { useNavItems } from "./nav";

const LANGUAGE_LABELS: Record<string, string> = { en: "English", sw: "Kiswahili" };

export function Shell({ children }: { children: ReactNode }) {
  const brand = useBrand();
  const { t, i18n } = useTranslation();
  const header = brand.header ? localised(brand, brand.header, i18n.language) : "";
  const title = brand.title ? localised(brand, brand.title, i18n.language) : brand.name;
  const { toggleColorScheme, colorScheme } = useMantineColorScheme();
  const session = useSession();
  const queryClient = useQueryClient();
  useEffect(() => {
    // The page title follows the chosen language (index.html has the default one).
    document.title = title;
  }, [title]);
  useEffect(() => {
    // Nothing personal stays cached once the session ends. Public data (keys
    // starting with "public": pricing, legal texts, testimonials) is kept, so
    // a public page's request is not cancelled when the first refresh settles.
    if (session.status === "signed-out") queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "public" });
  }, [session.status, queryClient]);
  return (
    <AppShell header={{ height: 64 }} padding="md">
      <a className="skip-link" href="#main">
        {t("skip_to_content")}
      </a>
      <AppShell.Header>
        <Container size="lg" h="100%">
          <Group h="100%" justify="space-between" wrap="nowrap">
            <Anchor component={Link} to="/" aria-label={t("nav.home")} underline="never" c="inherit" miw={0}>
              <Group gap="xs" wrap="nowrap">
                <Image src={brand.assets.logo} alt={brand.name} h={36} w="auto" fit="contain" />
                {header && (
                  // Wraps to two lines on narrow screens so the header controls keep their room.
                  <Text
                    fw={700}
                    lh={1.15}
                    fz={{ base: "xs", xs: "sm", sm: "lg" }}
                    maw={{ base: 112, xs: 190, sm: "none" }}
                    data-testid="brand-header"
                  >
                    {header}
                  </Text>
                )}
              </Group>
            </Anchor>
            <Group gap="sm" wrap="nowrap">
              {session.status === "signed-in" ? (
                <>
                  <AccountMenu />
                  {/* On phones the account menu holds it; the header has no room. */}
                  <SignOutButton />
                </>
              ) : (
                <Button component={Link} to="/signin" size="xs" variant="light" visibleFrom="xs">
                  {t("nav.sign_in")}
                </Button>
              )}
              <SegmentedControl
                size="xs"
                aria-label={t("nav.language")}
                value={i18n.language}
                onChange={(lng) => {
                  rememberLanguage(lng);
                  void i18n.changeLanguage(lng);
                }}
                data={brand.locales.map((l) => ({ value: l, label: LANGUAGE_LABELS[l] ?? l }))}
              />
              <ActionIcon
                variant="default"
                size="lg"
                aria-label={t("nav.theme")}
                aria-pressed={colorScheme === "dark"}
                onClick={toggleColorScheme}
              >
                <span aria-hidden="true">{colorScheme === "dark" ? "☀" : "☾"}</span>
              </ActionIcon>
            </Group>
          </Group>
        </Container>
      </AppShell.Header>
      {/* A full-height column: the page grows, so the footer sits at the bottom of short pages. */}
      <AppShell.Main id="main" tabIndex={-1} style={{ display: "flex", flexDirection: "column" }}>
        <Container size="lg" w="100%" style={{ flex: "1 0 auto" }}>
          {session.status === "signed-in" ? <TotpRequiredBanner /> : null}
          {session.status === "signed-in" ? <SmsSignInsBanner /> : null}
          <SetupGate>{children}</SetupGate>
        </Container>
        {/* Compact: links and copyright share a row on wide screens and wrap on phones. */}
        <Container size="lg" w="100%" component="footer" py="sm" mt="md">
          <Group justify="space-between" align="center" gap="xs" style={{ rowGap: 4 }}>
            <Group
              component="nav"
              gap="md"
              style={{ rowGap: 4 }}
              aria-label={t("nav.site_links")}
              data-testid="footer-links"
            >
              <Anchor component={Link} to="/pricing" size="sm">
                {t("nav.pricing")}
              </Anchor>
              <Anchor component={Link} to="/developers" size="sm">
                {t("nav.developers")}
              </Anchor>
              <Anchor component={Link} to="/terms" size="sm">
                {t("nav.terms")}
              </Anchor>
              <Anchor component={Link} to="/privacy" size="sm">
                {t("nav.privacy")}
              </Anchor>
            </Group>
            <Text size="sm" c="dimmed" data-testid="footer">
              {localised(brand, brand.footer, i18n.language)}
            </Text>
          </Group>
        </Container>
      </AppShell.Main>
    </AppShell>
  );
}

/** AccountMenu holds the signed-in pages; on small screens it collapses to an icon. */
/** useSignOut ends the session and goes to the sign-in page. */
function useSignOut() {
  const session = useSession();
  const navigate = useNavigate();
  return async () => {
    await session.signOut();
    navigate("/signin");
  };
}

function SignOutButton() {
  const { t } = useTranslation();
  const signOut = useSignOut();
  return (
    <Button size="xs" variant="default" visibleFrom="sm" onClick={() => void signOut()} data-testid="sign-out">
      {t("nav.sign_out")}
    </Button>
  );
}

function AccountMenu() {
  const { t } = useTranslation();
  const items = useNavItems();
  const signOut = useSignOut();
  return (
    <Menu position="bottom-end" withinPortal>
      <Menu.Target>
        <Button size="xs" variant="light" aria-label={t("nav.account")} data-testid="account-menu">
          <span aria-hidden="true">☰</span>
          <Text span size="xs" fw={600} ml={6} visibleFrom="xs">
            {t("nav.account")}
          </Text>
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item component={Link} to="/account">
          {t("nav.overview")}
        </Menu.Item>
        {items.map((i) => (
          <Menu.Item key={i.to} component={Link} to={i.to}>
            {i.label}
          </Menu.Item>
        ))}
        <Menu.Divider />
        <Menu.Item onClick={() => void signOut()} data-testid="menu-sign-out">
          {t("nav.sign_out")}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

/** TotpRequiredBanner tells the person the platform requires an authenticator app (B2). */
function TotpRequiredBanner() {
  const { t } = useTranslation();
  const me = useMe();
  const location = useLocation();
  if (!me.data?.totp_required || me.data.totp_enabled) return null;
  return (
    <Alert color="orange" role="alert" title={t("totp_banner.title")} mt="md" data-testid="totp-required-banner">
      <Text size="sm">{t("totp_banner.body")}</Text>
      {location.pathname !== "/security" && (
        <Button component={Link} to="/security" size="xs" mt="sm">
          {t("totp_banner.link")}
        </Button>
      )}
    </Alert>
  );
}

/**
 * SetupGate sends a session that may only set up an authenticator app (the
 * account used its sign-ins with an SMS code, ADR 0039) to the guided setup.
 */
function SetupGate({ children }: { children: ReactNode }) {
  const me = useMe();
  const location = useLocation();
  if (me.data?.totp_setup_required && !me.data.totp_enabled && location.pathname !== SETUP_AUTHENTICATOR_PATH) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`${SETUP_AUTHENTICATOR_PATH}?next=${next}`} replace />;
  }
  return <>{children}</>;
}

/** SmsSignInsBanner counts down the sign-ins with an SMS code left (ADR 0039). */
function SmsSignInsBanner() {
  const { t } = useTranslation();
  const me = useMe();
  const location = useLocation();
  const left = me.data?.sms_sign_ins_left;
  if (
    left === undefined ||
    me.data?.totp_enabled ||
    me.data?.totp_setup_required ||
    location.pathname === SETUP_AUTHENTICATOR_PATH
  ) {
    return null;
  }
  return (
    <Alert color="brand" mt="md" data-testid="sms-sign-ins-left" title={t("sms_left.title", { count: left })}>
      <Text size="sm">{t("sms_left.body")}</Text>
      <Button component={Link} to={SETUP_AUTHENTICATOR_PATH} size="xs" mt="sm">
        {t("sms_left.link")}
      </Button>
    </Alert>
  );
}
